import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormGroup, UntypedFormArray } from '@angular/forms';
import { matExpansionAnimations, MatExpansionPanelState } from '@angular/material/expansion';
import { FilterType } from '@app/core/models';
import { Block } from '@app/core/models/block/block.model';
import { FilterData } from '@app/core/services/api/api-filter.service';
import { ButtonHandlerService } from '@app/core/services/button-handler.service';
import { Styles } from '@core/interfaces/styles/styles.interface';
import { GlobalService } from '@core/services/common/global.service';
import { SubSink } from '@core/utils/subsink';
import { mergeAllStyles } from '@shared/helpers/block-helpers';
import { camelCase } from 'lodash';
import { filter } from 'rxjs/operators';

@Component({
  selector: 'app-filter-group-selector-overlay',
  templateUrl: './filter-group-selector-overlay.component.html',
  styleUrls: ['./filter-group-selector-overlay.component.scss'],
  animations: [matExpansionAnimations.bodyExpansion],
  providers: [ButtonHandlerService],
  standalone: false,
})
export class FilterGroupSelectorOverlayComponent implements OnInit, OnDestroy {
  @Input() outerStyle: any = {};
  @Input() innerStyle: any = {};
  @Input() controlType: FilterType;
  @Input() items: FilterData[];
  @Input() form: FormGroup;
  @Input() itemTemplate: Block;
  @Input() filterGroupSelector: ElementRef;
  @Input() filterGroupSelectorStyles: Styles;
  @Output() collapsed = new EventEmitter();
  @Output() itemChecked = new EventEmitter();
  protected scrollEnabled = false;
  protected scrollEnabledTimeout = null;
  protected expanded = false;
  private expandAnimationTime = 300;
  private subSink = new SubSink();
  expansionPanelOuterStyle: any = {};
  expansionPanelInnerStyle: any = {};

  get controlTypeArray() {
    return this.form.get(this.controlType) as UntypedFormArray;
  }

  constructor(private globalService: GlobalService) {
  }

  ngOnInit(): void {
    // little hack to fix the expand animation
    setTimeout(() => {
      this.startup();
    }, 0);
  }

  ngOnDestroy(): void {
    this.subSink.unsubscribe();
  }

  startup() {
    this.expanded = true;
    this.expansionPanelOuterStyle = this.outerStyle;
    this.expansionPanelInnerStyle = this.innerStyle;

    this.scrollEnabledTimeout = setTimeout(() => {
      this.scrollEnabled = true;
      this.scrollEnabledTimeout = null;
    }, this.expandAnimationTime);

    this.subSink.sink = this.globalService.pageWidthSubject
      .pipe(filter(() => this.filterGroupSelector?.nativeElement?.offsetWidth > 0))
      .subscribe(() => {
        this.expansionPanelOuterStyle.width = `${this.filterGroupSelector.nativeElement.offsetWidth}px`;
      });

    this.subSink.sink = this.globalService.mediaQuery.subscribe(mediaQuery => {
      const querySnakeCase = camelCase(mediaQuery.query);
      const mergedStyles = mergeAllStyles(this.filterGroupSelectorStyles, querySnakeCase);
      this.expansionPanelInnerStyle.width = mergedStyles.basic.width;
    });
  }

  collapse() {
    this.expanded = false;
    if (this.scrollEnabledTimeout !== null) {
      clearTimeout(this.scrollEnabledTimeout);
      this.scrollEnabledTimeout = null;
    }

    setTimeout(() => {
      this.scrollEnabled = false;
      this.collapsed.emit();
    }, this.expandAnimationTime);
  }

  getExpandedState(): MatExpansionPanelState {
    return this.expanded ? 'expanded' : 'collapsed';
  }

  getExpandedClass(): string {
    return this.expanded && this.scrollEnabled ? 'scroll-enabled' : '';
  }

  getItem(index: number): FormGroup {
    return this.controlTypeArray.at(index) as FormGroup;
  }
}
